<script lang="ts" setup>
import { Delta } from "@vueup/vue-quill";

const props = defineProps({
  position: {
    type: Number,
    required: true,
  },
  checklistCriteria: {
    type: Object as PropType<ChecklistCriteria>,
    required: true,
  },
});

const documentId = inject("documentId") as any;
const documentEvaluativeParagraphStore =
  useDocumentEvaluativeParagraphsStore(documentId);
const { getParagraphIdFromParagraphPosition } = storeToRefs(
  documentEvaluativeParagraphStore
);

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const { setAnnotationBeingEdited, createDefaultAnnotation } =
  useAnnotationFormState();

const paragraph = computed(() => {
  const paragraphId = getParagraphIdFromParagraphPosition.value(props.position);

  if (paragraphId == undefined) return;

  const documentParagraphStore = useDocumentParagraphStore(
    documentId,
    paragraphId
  );
  const { paragraph: matchingParagraph } = storeToRefs(documentParagraphStore);

  return matchingParagraph.value;
});

const createAnnotation = () => {
  // OK where do I, if at all, map a paragraph position to an id?

  if (paragraph.value == undefined) return;

  const tokens = paragraph.value.tokens;

  const annotation = createDefaultAnnotation();

  annotation.forceInline = true;
  annotation.forceAfterToken = true;
  annotation.isEndLine = true;
  annotation.lastTokenGlobalPosition = tokens[tokens.length - 1].globalPosition;
  annotation.paragraphId = paragraph.value.id;

  return annotation;
};

const annotateInstances = () => {
  setSelection(undefined, undefined);

  const commentOnlyAnnotation = createAnnotation();

  setAnnotationBeingEdited(documentId, commentOnlyAnnotation);
};

const { saveAnnotation } = useSaveAnnotation(documentId);

const { setSelection } = useDocumentSelectionStore(documentId);

const addAnnotation = (comment: string) => {
  setSelection(undefined, undefined);

  const commentOnlyAnnotation = createAnnotation();

  if (commentOnlyAnnotation == undefined) return;

  commentOnlyAnnotation.commentObject = new Delta().insert(comment).ops;
  commentOnlyAnnotation.quickComment = comment;

  if (props.checklistCriteria.useSmartAnnotations) {
    commentOnlyAnnotation.isSmartAnnotation = true;
  }

  saveAnnotation(
    commentOnlyAnnotation,
    useIsAnnotationCommentLong(commentOnlyAnnotation)
  ).then();

  setSelection(undefined, undefined);
};

const missingComment = computed(() => {
  return `missing ${props.checklistCriteria.label}`;
});

const noneFoundMessage = computed(() => {
  if (isTeacherViewMode.value == true) {
    return "none found";
  }
  
  // If edit submission is enabled add "or click Edit Submission";
  
  if (props.checklistCriteria.styleSection == "Dress-Ups") {
    return "Nothing is indicated. If you included this dress-up in your paragraph, click the word that should be underlined.";
  } else if (props.checklistCriteria.styleSection == "Sentence Openers") {
    return "Nothing is indicated. If you included this sentence opener in your paragraph, click the first word of the appropriate sentence.";
  } else if (props.checklistCriteria.styleSection == "Decorations") {
    return "You should have one decoration in each paragraph. To indicate additional decorations, highlight and select the group of text that should be italicized.";
  }
});
</script>

<template>
  <div class="flex flex-row items-center">
    <BasePopover
      :delay="{ show: 100, hide: 500 }"
      side="bottom"
      align="start"
      v-if="paragraph && isTeacherViewMode"
      :show-trigger-methods="['hover']"
      :hide-trigger-methods="['hover']"
    >
      <BaseIconButton @click.stop="annotateInstances" class="mr-2">
        <BaseIconCommentBox />
      </BaseIconButton>
      <template #content>
        <div class="flex flex-col p-1">
          <span
            @mousedown.prevent
            class="p-2 cursor-pointer hover:bg-light-default rounded"
            @click="addAnnotation(missingComment)"
          >
            {{ missingComment }}
          </span>
        </div>
      </template>
    </BasePopover>
    <span
      class="italic -mt-[2px] leading-[30px]"
      :class="{
        'text-warning-default': isTeacherViewMode,
        'text-danger-default': isTeacherViewMode != true,
      }"
    >
      {{ noneFoundMessage }}
    </span>
  </div>
</template>

<style scoped></style>
